import React, { useState, useEffect } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import Dialog from '@material-ui/core/Dialog';
import Tabs from '@material-ui/core/Tabs';
import Tab from '@material-ui/core/Tab';
import TextField from '@material-ui/core/TextField';
import Button from '@material-ui/core/Button';
import IconButton from '@material-ui/core/IconButton';
import Chip from '@material-ui/core/Chip';
import Tooltip from '@material-ui/core/Tooltip';
import Checkbox from '@material-ui/core/Checkbox';
import CloseIcon from '@material-ui/icons/Close';
import AutoAwesomeIcon from '@material-ui/icons/Flare';
import ImageIcon from '@material-ui/icons/Image';
import { generateQuirkyName } from '../../helpers/nameHelpers';

const useStyles = makeStyles({
  paper: { borderRadius: '14px', width: '420px', maxWidth: '95vw' },
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    position: 'relative',
    padding: '1rem',
    fontWeight: 700,
    fontSize: '1.05rem',
  },
  close: { position: 'absolute', right: '0.5rem', top: '0.5rem' },
  tabs: { minHeight: '40px', borderBottom: 'none' },
  tab: { minWidth: 'auto', minHeight: '40px', textTransform: 'none', fontWeight: 600 },
  body: { padding: '1.25rem 1.5rem' },
  label: { fontWeight: 700, fontSize: '0.85rem', margin: '0 0 0.4rem' },
  nameRow: { display: 'flex', alignItems: 'center', gap: '0.4rem' },
  tagList: { display: 'flex', flexWrap: 'wrap', gap: '0.35rem', marginTop: '0.5rem' },
  colorRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.6rem',
    marginBottom: '0.6rem',
  },
  swatch: { width: '28px', height: '28px', borderRadius: '6px', flexShrink: 0, border: '1px solid rgba(0,0,0,0.1)' },
  listRow: {
    display: 'flex',
    alignItems: 'center',
    padding: '0.4rem 0.6rem',
    borderRadius: '8px',
    cursor: 'pointer',
    '&:hover': { backgroundColor: 'rgba(0,0,0,0.04)' },
  },
  emptyRow: {
    padding: '0.75rem',
    textAlign: 'center',
    color: '#888',
    backgroundColor: 'rgba(0,0,0,0.03)',
    borderRadius: '8px',
  },
  addRow: { display: 'flex', gap: '0.5rem', marginTop: '0.75rem', alignItems: 'center' },
  proBadge: {
    fontSize: '0.6rem',
    fontWeight: 700,
    color: '#d81b60',
    backgroundColor: 'rgba(216,27,96,0.1)',
    borderRadius: '4px',
    padding: '1px 5px',
    marginLeft: '0.4rem',
  },
  imageDrop: {
    marginTop: '0.75rem',
    height: '160px',
    borderRadius: '10px',
    backgroundColor: 'rgba(0,0,0,0.04)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: '#bbb',
  },
  saveBtn: {
    margin: '0.5rem 1.5rem 1.5rem',
    padding: '0.7rem',
    fontWeight: 700,
    textTransform: 'none',
    fontSize: '1rem',
  },
});

const TAB_LABELS = ['Info', 'Colors', 'Projects', 'Collections', 'Image'];

/**
 * Coolors-style "Save Palette" modal with Info / Colors / Projects /
 * Collections / Image tabs and an AI-powered random name generator.
 */
function SavePaletteModal({
  open,
  onClose,
  swatches,
  projects,
  collections,
  onAddProject,
  onAddCollection,
  onSave,
  initial = null,
}) {
  const classes = useStyles();
  const [tab, setTab] = useState(0);
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [tags, setTags] = useState([]);
  const [tagInput, setTagInput] = useState('');
  const [labels, setLabels] = useState([]);
  const [projectIds, setProjectIds] = useState([]);
  const [collectionIds, setCollectionIds] = useState([]);
  const [newProject, setNewProject] = useState('');
  const [newCollection, setNewCollection] = useState('');
  // Snapshot of the swatches taken when the modal opens, so the Colors tab and
  // the saved payload stay aligned with the labels even if the parent re-renders
  // a fresh `swatches` array while the modal is open.
  const [frozenSwatches, setFrozenSwatches] = useState([]);

  // Reset the form only when the modal opens, so a parent re-render while it's
  // open (which produces a fresh `swatches` array) doesn't wipe the user's input.
  useEffect(() => {
    if (open) {
      setTab(0);
      setName((initial && initial.name) || '');
      setDescription((initial && initial.description) || '');
      setTags((initial && initial.tags) || []);
      setTagInput('');
      setFrozenSwatches(swatches);
      setLabels(
        swatches.map((s, i) =>
          initial && initial.labels && initial.labels[i] != null
            ? initial.labels[i]
            : s.name || ''
        )
      );
      setProjectIds((initial && initial.projectIds) || []);
      setCollectionIds((initial && initial.collectionIds) || []);
      setNewProject('');
      setNewCollection('');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // Use the frozen snapshot once captured; fall back to the live prop on first render.
  const activeSwatches = frozenSwatches.length ? frozenSwatches : swatches;

  const generateName = () => setName(generateQuirkyName());

  const addTag = () => {
    const t = tagInput.trim();
    if (t && !tags.includes(t)) setTags([...tags, t]);
    setTagInput('');
  };

  const toggleId = (list, setList, id) =>
    setList(list.includes(id) ? list.filter(x => x !== id) : [...list, id]);

  const handleAddProject = () => {
    const n = newProject.trim();
    if (!n) return;
    const project = onAddProject(n);
    if (project) setProjectIds(ids => [...ids, project.id]);
    setNewProject('');
  };

  const handleAddCollection = () => {
    const n = newCollection.trim();
    if (!n) return;
    const collection = onAddCollection(n);
    if (collection) setCollectionIds(ids => [...ids, collection.id]);
    setNewCollection('');
  };

  const handleSave = () => {
    const colors = activeSwatches.map((s, i) => ({
      name: (labels[i] || s.name || `color-${i + 1}`).trim(),
      color: s.hex,
    }));
    onSave({
      paletteName: name.trim() || generateQuirkyName(),
      description: description.trim(),
      tags,
      colors,
      projectIds,
      collectionIds,
    });
  };

  return (
    <Dialog open={open} onClose={onClose} classes={{ paper: classes.paper }}>
      <div className={classes.header}>
        Save Palette
        <IconButton size="small" className={classes.close} onClick={onClose} aria-label="Close">
          <CloseIcon fontSize="small" />
        </IconButton>
      </div>

      <Tabs
        value={tab}
        onChange={(e, v) => setTab(v)}
        className={classes.tabs}
        variant="scrollable"
        scrollButtons="off"
        indicatorColor="primary"
        textColor="primary"
      >
        {TAB_LABELS.map(l => (
          <Tab key={l} label={l} className={classes.tab} />
        ))}
      </Tabs>

      <div className={classes.body}>
        {tab === 0 && (
          <>
            <p className={classes.label}>Name</p>
            <div className={classes.nameRow}>
              <TextField
                fullWidth
                variant="outlined"
                size="small"
                placeholder="My new palette"
                value={name}
                onChange={e => setName(e.target.value)}
                inputProps={{ 'aria-label': 'Palette name' }}
              />
              <Tooltip title="Generate with AI" arrow>
                <IconButton onClick={generateName} aria-label="Generate name with AI" color="primary">
                  <AutoAwesomeIcon />
                </IconButton>
              </Tooltip>
            </div>

            <p className={classes.label} style={{ marginTop: '1rem' }}>
              Description
            </p>
            <TextField
              fullWidth
              variant="outlined"
              size="small"
              multiline
              minRows={3}
              value={description}
              onChange={e => setDescription(e.target.value)}
              inputProps={{ 'aria-label': 'Palette description' }}
            />

            <p className={classes.label} style={{ marginTop: '1rem' }}>
              Tags
            </p>
            <TextField
              fullWidth
              variant="outlined"
              size="small"
              placeholder="Add new"
              value={tagInput}
              onChange={e => setTagInput(e.target.value)}
              onKeyDown={e => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  addTag();
                }
              }}
              inputProps={{ 'aria-label': 'Add tag' }}
            />
            {tags.length > 0 && (
              <div className={classes.tagList}>
                {tags.map(t => (
                  <Chip key={t} label={t} size="small" onDelete={() => setTags(tags.filter(x => x !== t))} />
                ))}
              </div>
            )}
          </>
        )}

        {tab === 1 && (
          <>
            {activeSwatches.map((s, i) => (
              <div className={classes.colorRow} key={`${s.hex}-${i}`}>
                <div className={classes.swatch} style={{ backgroundColor: s.hex }} />
                <TextField
                  variant="outlined"
                  size="small"
                  placeholder="Label"
                  value={labels[i] || ''}
                  onChange={e => {
                    const next = [...labels];
                    next[i] = e.target.value;
                    setLabels(next);
                  }}
                  inputProps={{ 'aria-label': `Label for ${s.hex}` }}
                />
                <span style={{ fontFamily: 'monospace', color: '#666' }}>{s.hex.toUpperCase()}</span>
              </div>
            ))}
          </>
        )}

        {tab === 2 && (
          <>
            <p className={classes.label}>Select projects</p>
            {projects.length === 0 ? (
              <div className={classes.emptyRow}>No projects yet.</div>
            ) : (
              projects.map(p => (
                <div
                  key={p.id}
                  className={classes.listRow}
                  onClick={() => toggleId(projectIds, setProjectIds, p.id)}
                >
                  <Checkbox checked={projectIds.includes(p.id)} color="primary" size="small" />
                  {p.name}
                </div>
              ))
            )}
            <div className={classes.addRow}>
              <TextField
                fullWidth
                variant="outlined"
                size="small"
                placeholder="New project"
                value={newProject}
                onChange={e => setNewProject(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && handleAddProject()}
                inputProps={{ 'aria-label': 'New project name' }}
              />
              <Button onClick={handleAddProject} variant="outlined">
                Add
              </Button>
            </div>
          </>
        )}

        {tab === 3 && (
          <>
            <p className={classes.label}>Select collections</p>
            {collections.length === 0 ? (
              <div className={classes.emptyRow}>No collections yet.</div>
            ) : (
              collections.map(c => (
                <div
                  key={c.id}
                  className={classes.listRow}
                  onClick={() => toggleId(collectionIds, setCollectionIds, c.id)}
                >
                  <Checkbox checked={collectionIds.includes(c.id)} color="primary" size="small" />
                  {c.name}
                </div>
              ))
            )}
            <div className={classes.addRow}>
              <TextField
                fullWidth
                variant="outlined"
                size="small"
                placeholder="New collection"
                value={newCollection}
                onChange={e => setNewCollection(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && handleAddCollection()}
                inputProps={{ 'aria-label': 'New collection name' }}
              />
              <Button onClick={handleAddCollection} variant="outlined">
                Add
              </Button>
            </div>
          </>
        )}

        {tab === 4 && (
          <>
            <p className={classes.label}>
              Pair this palette with an image.
              <span className={classes.proBadge}>PRO</span>
            </p>
            <div className={classes.imageDrop}>
              <ImageIcon fontSize="large" />
            </div>
          </>
        )}
      </div>

      <Button
        variant="contained"
        color="primary"
        className={classes.saveBtn}
        onClick={handleSave}
        disableElevation
      >
        Save
      </Button>
    </Dialog>
  );
}

export default SavePaletteModal;
